import { useMemo, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ko } from 'date-fns/locale';

export const DATE_KEY_FORMAT = 'yyyy-MM-dd';

interface CalendarSingleSelectProps {
  selected: string | null;
  onChange: (dateKey: string) => void;
  minDate?: Date;
  maxDate?: Date;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

export function CalendarSingleSelect({ selected, onChange, minDate, maxDate }: CalendarSingleSelectProps) {
  const min = startOfDay(minDate ?? new Date());

  const [cursor, setCursor] = useState<Date>(() =>
    startOfMonth(selected ? parseISO(selected) : min),
  );

  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const gridEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [cursor]);

  const canGoPrev = !isSameMonth(cursor, min) && !isBefore(cursor, startOfMonth(min));

  return (
    <div className="select-none">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCursor((c) => addMonths(c, -1))}
          disabled={!canGoPrev}
          aria-label="이전 달"
          className="pressable flex h-9 w-9 items-center justify-center rounded-full text-ink-muted hover:bg-surface-muted disabled:opacity-30 focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          ‹
        </button>
        <span className="text-ui font-semibold text-ink">{format(cursor, 'yyyy년 M월', { locale: ko })}</span>
        <button
          type="button"
          onClick={() => setCursor((c) => addMonths(c, 1))}
          aria-label="다음 달"
          className="pressable flex h-9 w-9 items-center justify-center rounded-full text-ink-muted hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 text-center text-tag font-semibold text-ink-faint">
        {WEEKDAYS.map((w, i) => (
          <span key={w} className={i === 0 ? 'text-error/70' : undefined}>
            {w}
          </span>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-y-1">
        {days.map((d) => {
          const key = format(d, DATE_KEY_FORMAT);
          const inMonth = isSameMonth(d, cursor);
          const disabled = isBefore(d, min) || (maxDate ? isAfter(d, maxDate) : false);
          const on = selected === key;
          const isSunday = d.getDay() === 0;
          return (
            <div key={key} className="flex items-center justify-center py-0.5">
              <button
                type="button"
                disabled={disabled}
                aria-pressed={on}
                aria-label={format(d, 'M월 d일 (EEE)', { locale: ko })}
                onClick={() => onChange(key)}
                className={`flex h-10 w-10 items-center justify-center rounded-full text-ui font-semibold transition-all duration-150 ease-airbnb focus-visible:outline-none focus-visible:shadow-focus-ring ${
                  on
                    ? 'bg-primary text-white'
                    : disabled
                      ? 'cursor-not-allowed text-ink-disabled'
                      : isSameDay(d, min)
                        ? 'text-primary hover:bg-primary-soft'
                        : `${inMonth ? (isSunday ? 'text-error/80' : 'text-ink') : 'text-ink-disabled'} hover:bg-surface-muted`
                }`}
              >
                {format(d, 'd')}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
